/**
 * Singapore area (neighbourhood) catchments for the `/math-tuition/:area` cluster.
 *
 * What: The canonical table of area records — slug, display name, the postal
 *       sectors that define the catchment, and the MRT reality — plus the
 *       predicates that resolve a `SchoolRecord` list into one area.
 * Why: The area pages convert accidental page-1 matches (Google currently serves
 *      `/schools/woodlands-ring-secondary-school` for `primary math tuition
 *      woodlands`) into intentional ones. Every page lists the real schools in
 *      its catchment, so the resolution rule has to be accurate, not decorative.
 * When: Update when an area is added/removed or a catchment boundary changes.
 *
 * ## Why postal sectors and not `nearestMrt`
 *
 * The obvious matcher — substring-match `address` / `nearestMrt` — is wrong in
 * both directions on this dataset:
 *
 *   - False positives. `School of the Arts` sits at "1 **Z-UBI-R** Said Drive"
 *     (Dhoby Ghaut) and substring-matches "UBI", putting it in the Paya Lebar /
 *     Ubi catchment it is nowhere near.
 *   - False negatives / over-reach. `nearestMrt` is free text listing up to five
 *     stations: CHIJ Katong Convent reads "BEDOK MRT, KEMBANGAN MRT, EUNOS MRT,
 *     PAYA LEBAR MRT, MARINE TERRACE MRT", which would place one school in four
 *     different areas. Some records are empty or literally "Nil".
 *
 * The 6-digit postal code on the same record encodes the sector (first two
 * digits), which maps cleanly and stably onto Singapore towns. That is the
 * spine here. Consequences worth knowing, all verified against the catalog:
 *
 *   - Punggol Primary School (61 Hougang Ave 8) resolves to `hougang`, because
 *     that is where it physically is. Name-matching would have got this wrong.
 *   - Paya Lebar Methodist Girls' School (Lorong Ah Soo) resolves to `hougang`
 *     for the same reason. This is deliberate, not an oversight.
 *
 * ## Bundle-size contract
 *
 * This module must stay free of a *value* import of `SCHOOLS` — it is imported
 * by `shared/seo/publicMarketingRoutes.ts`, which ships in the client bundle,
 * and the school catalog is ~290KB of JSON. `SchoolRecord` is imported as a
 * type only (erased at compile time). Helpers that need the real catalog live
 * in `./schoolAreaMembership.js`, which is server/build-only.
 */

import type { SchoolRecord } from './schoolRecord.js';

/** Slugs of every published area page, ordered by search demand. */
export const AREA_SLUGS = Object.freeze([
  'woodlands',
  'paya-lebar',
  'hougang',
  'clementi',
  'ang-mo-kio',
  'tampines',
  'punggol',
  'yishun',
  'bedok',
  'sengkang',
  'bukit-batok',
  'katong-marine-parade',
  'serangoon',
  'toa-payoh',
  'sembawang',
] as const);

export type AreaSlug = (typeof AREA_SLUGS)[number];

export interface SchoolArea {
  slug: AreaSlug;
  /** Town name as a reader would say it, e.g. "Ang Mo Kio". Not a page headline. */
  displayName: string;
  /**
   * Postal sectors (first two digits of the 6-digit postal code) that define
   * this catchment. A school belongs to the area when its sector is listed.
   */
  postalSectors: readonly string[];
  /** Rail lines serving the area. Descriptive copy input for the area pages. */
  mrtLines: readonly string[];
  /** Stations in or bordering the catchment. */
  mrtStations: readonly string[];
}

/**
 * The 15 published area catchments.
 *
 * Order follows the programme plan's demand ranking so downstream listings and
 * internal-link blocks lead with the highest-impression areas.
 *
 * Every area here resolves at least `MIN_SCHOOLS_PER_AREA` schools — enforced by
 * `__tests__/schoolAreas.test.ts`, so an empty area page can never ship.
 * Jurong is a deliberate omission: 23 schools but near-zero *tuition* query
 * volume, so it is an expansion candidate rather than a wave-1 page. Dover /
 * one-north (ACS(I), UWCSEA, Tanglin Trust) is likewise held back rather than
 * folded into `clementi`, where listing a Portsdown Road school would be a
 * geography claim we cannot defend.
 */
export const SCHOOL_AREAS: readonly SchoolArea[] = Object.freeze([
  {
    slug: 'woodlands',
    displayName: 'Woodlands',
    postalSectors: ['73'],
    mrtLines: ['North South Line', 'Thomson–East Coast Line'],
    mrtStations: [
      'Woodlands',
      'Admiralty',
      'Marsiling',
      'Woodlands South',
      'Woodlands North',
    ],
  },
  {
    slug: 'paya-lebar',
    displayName: 'Paya Lebar',
    postalSectors: ['38', '39', '40'],
    mrtLines: ['East West Line', 'Circle Line', 'Downtown Line'],
    mrtStations: [
      'Paya Lebar',
      'Aljunied',
      'Dakota',
      'Eunos',
      'MacPherson',
      'Ubi',
    ],
  },
  {
    slug: 'hougang',
    displayName: 'Hougang',
    postalSectors: ['53'],
    mrtLines: ['North East Line', 'Circle Line'],
    mrtStations: ['Hougang', 'Kovan', 'Bartley', 'Serangoon'],
  },
  {
    slug: 'clementi',
    displayName: 'Clementi',
    postalSectors: ['12'],
    mrtLines: ['East West Line'],
    mrtStations: ['Clementi', 'Dover'],
  },
  {
    slug: 'ang-mo-kio',
    displayName: 'Ang Mo Kio',
    postalSectors: ['56'],
    mrtLines: ['North South Line', 'Thomson–East Coast Line'],
    mrtStations: ['Ang Mo Kio', 'Yio Chu Kang', 'Mayflower', 'Lentor'],
  },
  {
    slug: 'tampines',
    displayName: 'Tampines',
    postalSectors: ['52'],
    mrtLines: ['East West Line', 'Downtown Line'],
    mrtStations: ['Tampines', 'Tampines West', 'Tampines East', 'Simei'],
  },
  {
    slug: 'punggol',
    displayName: 'Punggol',
    postalSectors: ['82'],
    mrtLines: ['North East Line', 'Punggol LRT'],
    mrtStations: ['Punggol', 'Punggol Coast'],
  },
  {
    slug: 'yishun',
    displayName: 'Yishun',
    postalSectors: ['76'],
    mrtLines: ['North South Line'],
    mrtStations: ['Yishun', 'Khatib'],
  },
  {
    slug: 'bedok',
    displayName: 'Bedok',
    postalSectors: ['46', '47', '48'],
    mrtLines: ['East West Line', 'Downtown Line'],
    mrtStations: [
      'Bedok',
      'Bedok North',
      'Bedok Reservoir',
      'Tanah Merah',
      'Kembangan',
    ],
  },
  {
    slug: 'sengkang',
    displayName: 'Sengkang',
    postalSectors: ['54', '79'],
    mrtLines: ['North East Line', 'Sengkang LRT'],
    mrtStations: ['Sengkang', 'Fernvale', 'Compassvale'],
  },
  {
    slug: 'bukit-batok',
    displayName: 'Bukit Batok',
    postalSectors: ['65'],
    mrtLines: ['North South Line'],
    mrtStations: ['Bukit Batok', 'Bukit Gombak'],
  },
  {
    slug: 'katong-marine-parade',
    displayName: 'Katong & Marine Parade',
    postalSectors: ['42', '43', '44', '45'],
    mrtLines: ['Thomson–East Coast Line', 'Circle Line', 'East West Line'],
    mrtStations: [
      'Marine Parade',
      'Marine Terrace',
      'Tanjong Katong',
      'Katong Park',
      'Siglap',
      'Dakota',
      'Eunos',
    ],
  },
  {
    slug: 'serangoon',
    displayName: 'Serangoon',
    postalSectors: ['55'],
    mrtLines: ['North East Line', 'Circle Line'],
    mrtStations: ['Serangoon', 'Lorong Chuan', 'Bartley'],
  },
  {
    slug: 'toa-payoh',
    displayName: 'Toa Payoh',
    postalSectors: ['31'],
    mrtLines: ['North South Line', 'Circle Line', 'Thomson–East Coast Line'],
    mrtStations: ['Toa Payoh', 'Braddell', 'Caldecott'],
  },
  {
    slug: 'sembawang',
    displayName: 'Sembawang',
    postalSectors: ['75'],
    mrtLines: ['North South Line'],
    mrtStations: ['Sembawang', 'Canberra', 'Admiralty'],
  },
]);

/**
 * Floor on schools per area. An area page whose directory is empty (or nearly)
 * is a doorway page — it promises local coverage and delivers a stub. The
 * schoolAreas test enforces this against the real catalog.
 */
export const MIN_SCHOOLS_PER_AREA = 5;

const SCHOOL_AREA_BY_SLUG = new Map<string, SchoolArea>(
  SCHOOL_AREAS.map(area => [area.slug, area])
);

/** Base path for the area cluster. */
export const AREA_ROUTE_BASE_PATH = '/math-tuition' as const;

export const AREA_ROUTE_PATHS: readonly string[] = Object.freeze(
  AREA_SLUGS.map(slug => `${AREA_ROUTE_BASE_PATH}/${slug}`)
);

/** Matches any well-formed area route path. Shape-only — see `isAreaSlug` for membership. */
export const AREA_ROUTE_PATH_PATTERN =
  /^\/math-tuition\/[a-z0-9]+(?:-[a-z0-9]+)*$/u;

export function buildAreaRoutePath(slug: AreaSlug): string {
  return `${AREA_ROUTE_BASE_PATH}/${slug}`;
}

export function isAreaSlug(slug: unknown): slug is AreaSlug {
  return typeof slug === 'string' && SCHOOL_AREA_BY_SLUG.has(slug);
}

export function getSchoolAreaBySlug(slug: unknown): SchoolArea | null {
  if (typeof slug !== 'string') return null;
  return SCHOOL_AREA_BY_SLUG.get(slug) ?? null;
}

/**
 * Resolve the area a route path addresses, or null when it is not an area page.
 * Unknown slugs return null so `/math-tuition/atlantis` cannot render a shell.
 */
export function getSchoolAreaByRoutePath(
  routePath: unknown
): SchoolArea | null {
  if (typeof routePath !== 'string') return null;
  if (!routePath.startsWith(`${AREA_ROUTE_BASE_PATH}/`)) return null;
  return getSchoolAreaBySlug(
    routePath.slice(`${AREA_ROUTE_BASE_PATH}/`.length)
  );
}

/**
 * Postal sector of a school record.
 *
 * Four records in the catalog carry a 5-digit postal code (a leading zero lost
 * upstream — e.g. Cantonment Primary's "88256" is really 088256). Left-padding
 * to six digits before slicing keeps those schools resolvable if a future area
 * covers their sector, instead of silently dropping them.
 */
function getPostalSector(school: SchoolRecord): string {
  const digits = String(school.postalCode ?? '').replace(/\D/gu, '');
  if (!digits) return '';
  return digits.padStart(6, '0').slice(0, 2);
}

/** Whether a school sits inside an area's catchment. */
export function isSchoolInArea(
  school: SchoolRecord,
  area: SchoolArea
): boolean {
  const sector = getPostalSector(school);
  return sector !== '' && area.postalSectors.includes(sector);
}

/**
 * Filter a school list down to one area, preserving input order.
 *
 * Takes the list as a parameter rather than reaching for `SCHOOLS` so this
 * module stays client-safe (see the bundle-size contract above). Callers with
 * no list in hand should use `getSchoolsInArea` from `./schoolAreaMembership.js`.
 */
export function filterSchoolsInArea<T extends SchoolRecord>(
  schools: readonly T[],
  slug: unknown
): readonly T[] {
  const area = getSchoolAreaBySlug(slug);
  if (!area) return [];
  return schools.filter(school => isSchoolInArea(school, area));
}
